import { forwardRef, type SVGProps } from "react";

interface CopyPasteIconsProps extends SVGProps<SVGSVGElement> {
  color?: string;
  height?: number | string;
  width?: number | string;
}

export const CopyPasteIcons = forwardRef<SVGSVGElement, CopyPasteIconsProps>(
  ({ color = "white", height = 16, width = 16, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M10.75 4.75H12.75C13.855 4.75 14.75 5.645 14.75 6.75V12.75C14.75 13.855 13.855 14.75 12.75 14.75H6.75C5.645 14.75 4.75 13.855 4.75 12.75V10.75"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8.75 0.75H2.75C1.64543 0.75 0.75 1.64543 0.75 2.75V8.75C0.75 9.85457 1.64543 10.75 2.75 10.75H8.75C9.85457 10.75 10.75 9.85457 10.75 8.75V2.75C10.75 1.64543 9.85457 0.75 8.75 0.75Z"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  },
);
CopyPasteIcons.displayName = "CopyPasteIcons";

interface MessageIconProps extends SVGProps<SVGSVGElement> {
  color?: string;
}

export const MessageIcon = forwardRef<SVGSVGElement, MessageIconProps>(
  ({ color = "white", ...props }, ref) => {
    return (
      <svg
        {...props}
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M7.75 0.75C3.884 0.75 0.75 3.884 0.75 7.75C0.75 9.126 1.153 10.405 1.838 11.487L0.75 14.75L4.013 13.662C5.095 14.347 6.374 14.75 7.75 14.75C11.616 14.75 14.75 11.616 14.75 7.75C14.75 3.884 11.616 0.75 7.75 0.75Z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  },
);
MessageIcon.displayName = "MessageIcon";

interface NoImageIconProps extends SVGProps<SVGSVGElement> {
  color?: string;
  size?: string;
}

export const NoImageIcon = forwardRef<SVGSVGElement, NoImageIconProps>(
  ({ color = "white", size = "20", ...props }, ref) => {
    return (
      <svg
        ref={ref}
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        role="img"
        aria-label="No image icon"
      >
        <g clipPath="url(#clip0_3976_11011)">
          <path
            d="M4.18018 16.6508L10.9291 9.90083C11.7968 9.03306 13.2035 9.03306 14.0713 9.90083L16.9446 12.7742"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14.7223 3.05469H5.27789C4.05059 3.05469 3.05566 4.04961 3.05566 5.27691V14.7214C3.05566 15.9487 4.05059 16.9436 5.27789 16.9436H14.7223C15.9496 16.9436 16.9446 15.9487 16.9446 14.7214V5.27691C16.9446 4.04961 15.9496 3.05469 14.7223 3.05469Z"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.94455 9.44184C7.71162 9.44184 8.33344 8.82001 8.33344 8.05295C8.33344 7.28589 7.71162 6.66406 6.94455 6.66406C6.17749 6.66406 5.55566 7.28589 5.55566 8.05295C5.55566 8.82001 6.17749 9.44184 6.94455 9.44184Z"
            fill="white"
          />
          <line
            x1="1"
            y1="18.9393"
            x2="18.9393"
            y2="1"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </g>
        <defs>
          <clipPath id="clip0_3976_11011">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  },
);

NoImageIcon.displayName = "NoImageIcon";

interface QrCodeIconProps extends SVGProps<SVGSVGElement> {
  color?: string;
}

export const QrCodeIcon = forwardRef<SVGSVGElement, QrCodeIconProps>(
  ({ color = "white", ...props }, ref) => {
    return (
      <svg
        ref={ref}
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M2.24976 2.25H9.22534V9.22559H2.24976V2.25ZM3.79989 3.80013V7.67546H7.67521V3.80013H3.79989Z"
          fill={color}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.7754 2.25H17.751V9.22559H10.7754V2.25ZM12.3255 3.80013V7.67546H16.2008V3.80013H12.3255Z"
          fill={color}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M2.24976 10.7773H9.22534V17.7529H2.24976V10.7773ZM3.79989 12.3275V16.2028H7.67521V12.3275H3.79989Z"
          fill={color}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.7754 10.7773H17.751V12.3275H16.2008V14.6527H17.751V16.2028H14.6507V13.8776H12.3255V12.3275H10.7754V10.7773Z"
          fill={color}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.3255 13.875V16.2002H14.6507V17.7503H10.7754V13.875H12.3255Z"
          fill={color}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.9624 4.96094H6.52028V6.51107H4.9624V4.96094Z"
          fill={color}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.9624 13.4883H6.52028V15.0384H4.9624V13.4883Z"
          fill={color}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.4807 4.96094H15.0386V6.51107H13.4807V4.96094Z"
          fill={color}
        />
      </svg>
    );
  },
);
QrCodeIcon.displayName = "QrCodeIcon";

interface SharedDevosIconProps extends SVGProps<SVGSVGElement> {
  color?: string;
}

export const SharedDevosIcon = forwardRef<SVGSVGElement, SharedDevosIconProps>(
  ({ color = "white", ...props }, ref) => {
    return (
      <svg
        ref={ref}
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M14.4715 13.1839V8.70289C14.4715 7.66085 13.7058 6.81611 12.7613 6.81611H5.5285M14.4715 13.1839V14.3632C14.4715 15.4053 13.7058 16.25 12.7613 16.25H4.21021C3.26569 16.25 2.5 15.4053 2.5 14.3632V8.70289C2.5 7.66085 3.26569 6.81611 4.21021 6.81611H5.5285M14.4715 13.1839H15.7898C16.7343 13.1839 17.5 12.3392 17.5 11.2971V5.63678C17.5 4.59474 16.7343 3.75 15.7898 3.75H7.23872C6.29419 3.75 5.5285 4.59474 5.5285 5.63678V6.81611"
          stroke={color}
          strokeWidth="1.5"
        />
      </svg>
    );
  },
);
SharedDevosIcon.displayName = "SharedDevosIcon";

interface SharingIconProps extends SVGProps<SVGSVGElement> {
  color?: string;
  size?: string;
}

export const SharingIcon = forwardRef<SVGSVGElement, SharingIconProps>(
  ({ color = "white", size = "16", ...props }, ref) => {
    return (
      <svg
        ref={ref}
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M2.5 11.9616L2.5 15C2.5 16.5533 3.4467 17.5 5 17.5L15 17.5C16.5533 17.5 17.5 16.5533 17.5 15L17.5 11.9616M13.75 6.25L10 2.5M10 2.5L6.25 6.25M10 2.5L10 13.192"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  },
);
SharingIcon.displayName = "SharingIcon";

interface UsersIconProps extends SVGProps<SVGSVGElement> {
  color?: string;
}

export const UsersIcon = forwardRef<SVGSVGElement, UsersIconProps>(
  ({ color = "white", ...props }, ref) => {
    return (
      <svg
        {...props}
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M6.5 11C7.88071 11 9 9.88071 9 8.5C9 7.11929 7.88071 6 6.5 6C5.11929 6 4 7.11929 4 8.5C4 9.88071 5.11929 11 6.5 11Z"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.5 8C14.8807 8 16 6.88071 16 5.5C16 4.11929 14.8807 3 13.5 3C12.1193 3 11 4.11929 11 5.5C11 6.88071 12.1193 8 13.5 8Z"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10.875 11.845C11.614 11.313 12.52 11 13.5 11C15.459 11 17.126 12.252 17.744 14"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2.2561 17C2.8741 15.252 4.5411 14 6.5001 14C8.4591 14 10.1261 15.252 10.7441 17"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  },
);
UsersIcon.displayName = "UsersIcon";

export const MeditationIcon = forwardRef<
  SVGSVGElement,
  SVGProps<SVGSVGElement>
>(({ color = "white", ...props }, ref) => {
  return (
    <svg
      {...props}
      ref={ref}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Meditation</title>
      <path
        d="M10 1C5.02318 1 1 5.02632 1 10C1 14.9737 5.02318 19 10 19C14.9768 19 19 14.9737 19 10C19 5.02632 14.9768 1 10 1ZM10 17.6678C5.76821 17.6678 2.31126 14.2336 2.31126 10C2.31126 5.76645 5.76821 2.33224 10 2.33224C14.2318 2.33224 17.6887 5.76645 17.6887 10C17.6887 14.2336 14.2318 17.6678 10 17.6678Z"
        fill={color}
      />
      <path
        d="M12.7772 7.4867C12.9712 7.4867 13.1653 7.42244 13.2947 7.26177L14.3298 6.23348C14.6209 5.94427 14.6209 5.49439 14.3298 5.20519C14.0387 4.91598 13.5858 4.91598 13.2947 5.20519L12.2596 6.23348C11.9685 6.52268 11.9685 6.97256 12.2596 7.26177C12.389 7.42244 12.5831 7.4867 12.7772 7.4867Z"
        fill={color}
      />
      <path
        d="M6.24294 12.2755L5.20784 13.3038C4.91672 13.593 4.91672 14.0429 5.20784 14.3321C5.33723 14.4606 5.53131 14.557 5.72539 14.557C5.91947 14.557 6.11356 14.4927 6.24294 14.3321L7.27804 13.3038C7.56916 13.0146 7.56916 12.5647 7.27804 12.2755C6.98692 11.9863 6.53406 11.9863 6.24294 12.2755Z"
        fill={color}
      />
      <path
        d="M15.4941 9.0625H14.0385C13.6504 9.0625 13.3269 9.38384 13.3269 9.76945C13.3269 10.1551 13.6504 10.4764 14.0385 10.4764H15.4941C15.8823 10.4764 16.2058 10.1551 16.2058 9.76945C16.2058 9.38384 15.8823 9.0625 15.4941 9.0625Z"
        fill={color}
      />
      <path
        d="M6.24288 9.76945C6.24288 9.38384 5.91942 9.0625 5.53125 9.0625H4.07564C3.68748 9.0625 3.36401 9.38384 3.36401 9.76945C3.36401 10.1551 3.68748 10.4764 4.07564 10.4764H5.53125C5.91942 10.4764 6.24288 10.1551 6.24288 9.76945Z"
        fill={color}
      />
      <path
        d="M13.2947 12.2755C13.0036 11.9863 12.5507 11.9863 12.2596 12.2755C11.9685 12.5647 11.9685 13.0146 12.2596 13.3038L13.2947 14.3321C13.4241 14.4606 13.6182 14.557 13.8122 14.557C14.0063 14.557 14.1681 14.4927 14.3298 14.3321C14.6209 14.0429 14.6209 13.593 14.3298 13.3038L13.2947 12.2755Z"
        fill={color}
      />
      <path
        d="M6.24294 5.20519C5.95182 4.91598 5.49896 4.91598 5.20784 5.20519C4.91672 5.49439 4.91672 5.94427 5.20784 6.23348L6.24294 7.26177C6.37233 7.3903 6.56641 7.4867 6.76049 7.4867C6.95457 7.4867 7.11631 7.42244 7.27804 7.26177C7.56916 6.97256 7.56916 6.52268 7.27804 6.23348L6.24294 5.20519Z"
        fill={color}
      />
      <path
        d="M9.76876 13.3047C9.3806 13.3047 9.05713 13.626 9.05713 14.0116V15.4577C9.05713 15.8433 9.3806 16.1646 9.76876 16.1646C10.1569 16.1646 10.4804 15.8433 10.4804 15.4577V14.0116C10.4804 13.626 10.1569 13.3047 9.76876 13.3047Z"
        fill={color}
      />
      <path
        d="M9.76876 3.34375C9.3806 3.34375 9.05713 3.66509 9.05713 4.0507V5.49673C9.05713 5.88234 9.3806 6.20368 9.76876 6.20368C10.1569 6.20368 10.4804 5.88234 10.4804 5.49673V4.0507C10.4804 3.66509 10.1569 3.34375 9.76876 3.34375Z"
        fill={color}
      />
    </svg>
  );
});
MeditationIcon.displayName = "MeditationIcon";
